// Local backup/archiving export (Axis 7). Boundary-adjacent module — reuses
// history.ts's existing read path (no direct localStorage access here) and
// triggers a plain browser file download. Deliberately client-side only: no
// OneDrive/Graph upload, no new permission scope (see docs/superpowers/plans/
// 2026-07-05-bloc-b-copilot-roadmap.md, Axis 7). Exports only what this app
// itself stores (conversation turns + AI outputs) — never raw email bodies,
// which are the mailbox's data, not this add-in's to export.

import { listConversations, type ConversationRecord } from "./history";
import { debugLog } from "./debug";

/** Bumped only if the exported shape changes in a way a consumer must know about. */
export const ARCHIVE_SCHEMA_VERSION = 1;

export interface HistoryExport {
  schemaVersion: number;
  /** ISO-8601 timestamp of when the export was generated. */
  exportedAt: string;
  conversations: ConversationRecord[];
}

/** Builds the exportable snapshot from history.ts's existing store. Pure —
 * no I/O beyond the read, so it's separately testable from the download side effect. */
export function buildHistoryExport(now: () => Date = () => new Date()): HistoryExport {
  return {
    schemaVersion: ARCHIVE_SCHEMA_VERSION,
    exportedAt: now().toISOString(),
    conversations: listConversations(),
  };
}

/** Filesystem-safe date stamp for the download filename, e.g. "2026-07-20". */
function fileDateStamp(now: () => Date): string {
  return now().toISOString().slice(0, 10);
}

/**
 * Serializes the full conversation history to pretty-printed JSON and
 * triggers a browser download (Blob + object URL + a temporary `<a
 * download>` click — no new dependency). Best-effort: a failure to create
 * the object URL or append/click the anchor is logged, not thrown, so a
 * flaky DOM state can't crash the caller (same posture as history.ts's own
 * read/write helpers).
 */
export function exportHistoryAsJson(now: () => Date = () => new Date()): void {
  const data = buildHistoryExport(now);
  const json = JSON.stringify(data, null, 2);

  try {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `ai-assistant-history-export-${fileDateStamp(now)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (e) {
    debugLog("archive:export-failed", e instanceof Error ? e.name : "unknown");
  }
}
